import React, { useEffect, useState } from 'react';
import ChatMenu from '../chat/ChatMenu';
import { subscriptionService, SubscriptionDetails, SubscriptionResponse } from '../../services/subscriptionService';
import { paymentService } from '../../services/paymentService';
import { authUtils } from '../../utils/auth';

const Subscription: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    try {
      const token = authUtils.getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await subscriptionService.getSubscriptionDetails(token);
      setSubscriptionData(response);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscriptionData({
        success: false,
        message: 'Failed to load subscription details',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeNow = async () => {
    setProcessingPayment(true);
    try {
      const token = authUtils.getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Initialize checkout
      const checkoutResponse = await paymentService.initiateCheckout(token, {
        billing_cycle: 'monthly',
        currency: 'USD',
      });

      if (!checkoutResponse.success || !checkoutResponse.checkout_token) {
        throw new Error(checkoutResponse.message || 'Failed to initialize checkout');
      }

      // Open Freemius checkout modal
      await paymentService.openCheckoutModal(
        checkoutResponse,
        async (response) => {
          // Payment successful
          console.log('Payment completed:', response);

          // Wait a moment for the webhook to be processed
          setTimeout(() => {
            fetchSubscriptionDetails();
          }, 3000);
        },
        () => {
          // Payment canceled
          console.log('Payment canceled');
          setProcessingPayment(false);
        }
      );
    } catch (error) {
      console.error('Error during checkout:', error);
      alert('Failed to initialize checkout. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    const currentSub = getCurrentSubscription();
    if (!currentSub || !currentSub.subscription_id) {
      alert('No active subscription found to cancel');
      return;
    }

    setCancellingSubscription(true);
    try {
      const token = authUtils.getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await paymentService.cancelSubscription(token, currentSub.subscription_id);

      if (result.success) {
        alert('Subscription canceled successfully');
        setShowCancelConfirm(false);
        // Refresh subscription details
        fetchSubscriptionDetails();
      } else {
        alert(result.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getCurrentSubscription = (): SubscriptionDetails & { subscription_id?: string } | null => {
    if (!subscriptionData?.subscription_details || subscriptionData.subscription_details.length === 0) {
      return null;
    }
    // Assuming the first subscription is the current one
    // The subscription_id will be added by the backend when integrated
    return {
      ...subscriptionData.subscription_details[0],
      subscription_id: (subscriptionData.subscription_details[0] as any).subscription_id,
    };
  };

  const getHistorySubscriptions = (): SubscriptionDetails[] => {
    if (!subscriptionData?.subscription_details || subscriptionData.subscription_details.length <= 1) {
      return [];
    }
    return subscriptionData.subscription_details.slice(1);
  };

  const renderSubscriptionStatus = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jobesta-blue"></div>
        </div>
      );
    }

    if (!subscriptionData?.success) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-jobesta p-6 text-center">
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-700">{subscriptionData?.message || 'Failed to load subscription details'}</p>
        </div>
      );
    }

    const currentSub = getCurrentSubscription();

    if (subscriptionData.subscription_status === 'na') {
      return (
        <div className="bg-jobesta-gray-30 rounded-jobesta p-8 text-center">
          <svg className="w-16 h-16 text-jobesta-gray-60 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          <h3 className="text-xl font-semibold text-jobesta-gray-90 mb-2">No Active Subscription</h3>
          <p className="text-jobesta-gray-70 mb-6">Get access to premium features with a subscription</p>
          <button
            onClick={handleSubscribeNow}
            disabled={processingPayment}
            className="bg-jobesta-blue text-white px-6 py-3 rounded-jobesta hover:bg-jobesta-darkblue transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processingPayment ? 'Processing...' : 'Subscribe Now'}
          </button>
        </div>
      );
    }

    if (subscriptionData.subscription_status === 'expired' && currentSub) {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-jobesta p-8 text-center">
          <svg className="w-16 h-16 text-orange-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-semibold text-orange-900 mb-2">Subscription Expired</h3>
          <p className="text-orange-700 mb-6">
            Your subscription expired on {formatDate(currentSub.expiration_date)}
          </p>
          <button
            onClick={handleSubscribeNow}
            disabled={processingPayment}
            className="bg-jobesta-blue text-white px-6 py-3 rounded-jobesta hover:bg-jobesta-darkblue transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processingPayment ? 'Processing...' : 'Renew Subscription'}
          </button>
        </div>
      );
    }

    if (subscriptionData.subscription_status === 'active' && currentSub) {
      return (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-jobesta p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center mb-2">
                  <svg className="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-green-700 font-semibold text-lg">Active Subscription</span>
                </div>
                <p className="text-green-600">Your subscription is active and in good standing</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-jobesta-gray-40 rounded-jobesta p-6">
            <h3 className="text-lg font-semibold text-jobesta-gray-90 mb-4">Subscription Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-jobesta-gray-60">Package</p>
                <p className="font-medium text-jobesta-gray-90">{currentSub.package_name}</p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Billing Cycle</p>
                <p className="font-medium text-jobesta-gray-90 capitalize">{currentSub.billing_cycle}</p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Amount</p>
                <p className="font-medium text-jobesta-gray-90">
                  {formatCurrency(currentSub.payment_amount, currentSub.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Payment Method</p>
                <p className="font-medium text-jobesta-gray-90 capitalize">{currentSub.payment_processor}</p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Start Date</p>
                <p className="font-medium text-jobesta-gray-90">{formatDate(currentSub.start_date)}</p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Expiration Date</p>
                <p className="font-medium text-jobesta-gray-90">{formatDate(currentSub.expiration_date)}</p>
              </div>
            </div>

            {currentSub.subscription_id && (
              <div className="mt-6 pt-6 border-t border-jobesta-gray-40">
                {!showCancelConfirm ? (
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    Cancel Subscription
                  </button>
                ) : (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-jobesta-gray-70">Are you sure?</span>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                    >
                      {cancellingSubscription ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="text-jobesta-gray-60 hover:text-jobesta-gray-70 text-sm font-medium"
                    >
                      No, Keep It
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderHistory = () => {
    const history = getHistorySubscriptions();

    if (history.length === 0) {
      return (
        <div className="text-center py-8 text-jobesta-gray-60">
          No subscription history available
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {history.map((sub, index) => (
          <div key={index} className="bg-white border border-jobesta-gray-40 rounded-jobesta p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-jobesta-gray-60">Package</p>
                <p className="font-medium text-jobesta-gray-90">{sub.package_name}</p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Amount</p>
                <p className="font-medium text-jobesta-gray-90">
                  {formatCurrency(sub.payment_amount, sub.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Period</p>
                <p className="font-medium text-jobesta-gray-90">
                  {formatDate(sub.start_date)} - {formatDate(sub.expiration_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-jobesta-gray-60">Status</p>
                <p className="font-medium text-jobesta-gray-90">
                  {new Date(sub.expiration_date) > new Date() ? (
                    <span className="text-green-600">Active</span>
                  ) : (
                    <span className="text-jobesta-gray-60">Expired</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const hasHistory = subscriptionData?.subscription_details && subscriptionData.subscription_details.length > 1;

  return (
    <div className="flex h-screen bg-jobesta-gray-20">
      <ChatMenu />

      <div className="flex-1 overflow-auto">
        <div className="container mx-auto px-4 py-8 max-w-4xl ph-no-capture">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-jobesta-gray-90 mb-2">Subscription Management</h1>
            <p className="text-jobesta-gray-70">Manage your subscription and billing information</p>
          </div>

          {hasHistory && (
            <div className="mb-6">
              <div className="flex space-x-1 bg-jobesta-gray-30 rounded-jobesta p-1">
                <button
                  onClick={() => setActiveTab('current')}
                  className={`flex-1 px-4 py-2 rounded-jobesta font-medium transition-colors ${
                    activeTab === 'current'
                      ? 'bg-white text-jobesta-blue shadow-sm'
                      : 'text-jobesta-gray-70 hover:text-jobesta-blue'
                  }`}
                >
                  Current Subscription
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`flex-1 px-4 py-2 rounded-jobesta font-medium transition-colors ${
                    activeTab === 'history'
                      ? 'bg-white text-jobesta-blue shadow-sm'
                      : 'text-jobesta-gray-70 hover:text-jobesta-blue'
                  }`}
                >
                  Subscription History
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-jobesta shadow-jobesta-card p-6">
            {activeTab === 'current' ? renderSubscriptionStatus() : renderHistory()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;